import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  METHODS, METHOD_LABEL, isoDay, suggestionsQuery, transactionsQuery, accountsQuery,
  type BankTransaction, type MatchKind, type PayMethod,
} from './adminApi';

/**
 * Popover "Incassa" / "Paga": pochi campi, solo l'essenziale.
 *   data (default oggi) · metodo · per Bonifico/Carta/Addebito un select dei
 *   movimenti Qonto compatibili (suggestions dell'API quando la voce esiste:
 *   rata fattura, rata fornitore, rata tassa, voce di cassa; per le ricorrenti,
 *   che non hanno ancora una voce, i movimenti in uscita non abbinati che
 *   citano la controparte).
 * Scegliere un movimento porta la data a quella del movimento: la verità è
 * l'estratto conto, non il giorno in cui lo si registra.
 */
export interface SettleValue {
  paidDate: string;
  method: PayMethod;
  bankTransactionId: string | null;
  accountId: string | null;
  /** Solo per fatture emesse/ricevute: perché la si chiude qui e non su FiC. */
  localPaidNote?: string | null;
}

export function SettlePopover({ mode, amount, target, counterparty, side, pending, withNote, onConfirm, onClose }: {
  mode: 'incassa' | 'paga';
  amount: number;
  /** Voce da saldare: abilita le suggestions dell'API. */
  target?: { kind: MatchKind; id: string };
  /** Senza target: filtra i movimenti non abbinati per controparte. */
  counterparty?: string;
  side?: 'DEBIT' | 'CREDIT';
  pending?: boolean;
  /**
   * Mostra il campo nota. Si accende solo dove la nota serve davvero, cioè sulle
   * rate di fatture emesse e ricevute: quelle vivono anche su FattureInCloud e
   * chiuderle qui crea una differenza da spiegare (tab "Chiuse qui"). Tasse,
   * ricorrenti e contanti su FiC non ci sono: niente da giustificare.
   */
  withNote?: boolean;
  onConfirm: (v: SettleValue) => void;
  onClose: () => void;
}) {
  const [paidDate, setPaidDate] = useState(isoDay());
  const [method, setMethod] = useState<PayMethod>(mode === 'incassa' ? 'BONIFICO' : 'BONIFICO');
  const [txId, setTxId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [note, setNote] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: accounts } = useQuery(accountsQuery);
  const wantsBank = method === 'BONIFICO' || method === 'CARTA' || method === 'RIBA' || method === 'ADDEBITO';

  // Suggerimenti: voce esistente → endpoint dedicato; altrimenti movimenti non abbinati per controparte
  const { data: suggested } = useQuery({ ...suggestionsQuery(target ?? { kind: 'INVOICE_PAYMENT', id: '' }), enabled: !!target && wantsBank });
  const { data: txPage } = useQuery({
    ...transactionsQuery({ side: side ?? 'DEBIT', unmatched: true, search: counterparty?.trim() || undefined, pageSize: 8 }),
    enabled: !target && wantsBank,
  });
  const candidates: BankTransaction[] = target ? (suggested ?? []) : (txPage?.items ?? []);

  // Conto di default: cassa se contanti, altrimenti il primo conto Qonto
  useEffect(() => {
    if (!accounts?.length) return;
    const want = method === 'CONTANTI' ? accounts.find((a) => a.provider === 'CASH') : accounts.find((a) => a.provider === 'QONTO');
    setAccountId(want?.id ?? '');
  }, [accounts, method]);

  const pickTx = (id: string) => {
    setTxId(id);
    const tx = candidates.find((t) => t.id === id);
    if (tx?.settledAt) setPaidDate(tx.settledAt.slice(0, 10));
  };

  const submit = () => {
    if (pending || !paidDate) return;
    onConfirm({ paidDate, method, bankTransactionId: txId || null, accountId: accountId || null, localPaidNote: note.trim() || null });
  };

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div ref={ref} className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label={mode === 'incassa' ? 'Registra incasso' : 'Registra pagamento'}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}>
        <div className="adm-pop-hd">
          <span className="t-micro">{mode === 'incassa' ? 'Incassa' : 'Paga'}</span>
          <span className="mono font-semibold">€ {fmtEur(amount, true)}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Data</label>
          <input className="inp inp-sm mono" type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} autoFocus />
          <label className="field-label">Metodo</label>
          <select className="inp inp-sm" value={method} onChange={(e) => { setMethod(e.target.value as PayMethod); setTxId(''); }}>
            {METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
          </select>
          {wantsBank && (
            <>
              <label className="field-label">Movimento</label>
              <select className="inp inp-sm" value={txId} onChange={(e) => pickTx(e.target.value)}>
                <option value="">{candidates.length ? 'Nessuno (solo registra)' : 'Nessun movimento compatibile'}</option>
                {candidates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {fmtDate(t.settledAt)} · € {fmtEur(t.amount, true)} · {(t.counterparty || t.label).slice(0, 40)}
                  </option>
                ))}
              </select>
            </>
          )}
          {withNote && (
            <>
              <label className="field-label">Nota</label>
              <input
                className="inp inp-sm"
                type="text"
                maxLength={500}
                placeholder="perché la chiudi qui (facoltativo)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </>
          )}
          {!wantsBank && (accounts?.length ?? 0) > 1 && (
            <>
              <label className="field-label">Conto</label>
              <select className="inp inp-sm" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {(accounts ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </>
          )}
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm btn-primary" disabled={pending || !paidDate} onClick={submit}>
            <Icon name="check" size={10} /> {mode === 'incassa' ? 'Incassa' : 'Paga'}
          </button>
        </div>
      </div>
    </>
  );
}
